/**
 * Monster Onesie — tier selector + in-tier configurator (BRIEF §5.4, §5.4b).
 *
 * All logic is client-side and driven by a JSON payload rendered by
 * sections/monster-product.liquid. Initialises when visible.
 *
 * State model, and the two rules that matter:
 *
 *   1. Per-unit {color, size} lives in one array sized to the LARGEST tier and
 *      is never truncated. Switching Squad -> Solo -> Squad must return the
 *      same four monsters, so the array outlives the selection.
 *   2. Solo is the resting state. The mockup initialises `ti = 1` and then
 *      calls setTier(0) on the last line, which is easy to misread as "Duo is
 *      the default". It is not — §5.4 is explicit that defaulting to Duo is a
 *      "helpful" change nobody asked for.
 *
 * Add-on rows: the comb row is informational only. Every variant already
 * includes a comb, so that row changes no state and posts nothing. The
 * slippers row is the real control and is ALL-OR-NOTHING across the order's
 * units — it flips every line's Set value together (§5.4b). There is no
 * mutual-exclusion logic; v1's either/or rule died with the combo row.
 */

/**
 * Prices are recomputed live (tier x slippers), so they cannot be pre-rendered
 * by Liquid's `money` filter. Format with the shop's own currency instead of
 * assuming dollars — this store is USD today, but a hardcoded `$` is the kind
 * of thing that survives a currency change silently.
 */
function makeMoney(currency) {
  let format;
  try {
    format = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: currency || 'USD',
    });
  } catch {
    format = null;
  }

  return (cents) => (format ? format.format(cents / 100) : `$${(cents / 100).toFixed(2)}`);
}

class MonsterConfigurator extends HTMLElement {
  /** @type {any} */
  #data = null;

  /** @type {{color: string, size: string}[]} */
  #units = [];

  #tierIndex = 0;
  #slippers = false;
  #started = false;
  #busy = false;

  /** Customer-facing failure copy; the raw exception goes to the console. */
  #errorMessage = "Something went wrong — nothing was added. Try again.";

  /** Shown while the add is in flight. */
  #pendingLabel = "Adding…";

  /** The merchant-set label, captured before we start appending the total. */
  #atcLabelBase = "Add to cart";

  /** Last colour broadcast to the gallery, so we only announce real changes. */
  #announcedColor = null;

  /** @type {(cents: number) => string} Replaced with the real formatter in #start. */
  #money = (cents) => `$${(cents / 100).toFixed(2)}`;

  connectedCallback() {
    const payload = this.querySelector('[data-monster-config]');
    if (!payload?.textContent) return;

    try {
      this.#data = JSON.parse(payload.textContent);
    } catch {
      return;
    }

    if (!this.#data?.tiers?.length || !this.#data?.colors?.length) return;

    // Initialise immediately, deliberately NOT behind an IntersectionObserver.
    //
    // §1 asks for interactive pieces to initialise when visible, and that is
    // right for decorative ones — the googly eyes still do it. This is the
    // buy box. Gating it cost real reliability twice: IntersectionObserver
    // callbacks belong to the rendering lifecycle, which browsers suspend for
    // background and prerendered tabs, so a restored tab showed tier cards
    // that did nothing. A viewport pre-check only narrowed the window, since
    // whether the buy box starts on screen depends on how the gallery lays
    // out once images load.
    //
    // The work here is parsing one JSON payload and building three cards.
    // Deferring that saves nothing worth a class of silent failures on the
    // one component the purchase depends on.
    this.#start();
  }

  #start() {
    if (this.#started) return;
    this.#started = true;

    const { colors, sizes, defaultColors, tiers } = this.#data;
    const maxUnits = Math.max(...tiers.map((tier) => tier.units));

    // Staggered defaults (§5.4): Swamp, Cloud, Grape, Midnight. Beyond the
    // supplied list, cycle rather than repeating one colour four times.
    for (let i = 0; i < maxUnits; i++) {
      // The setting is a comma-separated text field, so these arrive as
      // strings. Array indexing would coerce them anyway; being explicit
      // stops the arithmetic below from silently concatenating instead.
      const colorIndex = Number(defaultColors[i] ?? defaultColors[i % defaultColors.length] ?? 0);
      this.#units.push({
        color: colors[colorIndex]?.name ?? colors[0].name,
        size: sizes[Math.min(1, sizes.length - 1)], // M where it exists
      });
    }

    if (this.#data.errorMessage) this.#errorMessage = this.#data.errorMessage;
    if (this.#data.pendingLabel) this.#pendingLabel = this.#data.pendingLabel;

    const atcLabel = this.querySelector("[data-atc-label]");
    if (atcLabel) this.#atcLabelBase = atcLabel.textContent.trim();

    this.#money = makeMoney(this.#data.currency);
    this.#tierIndex = this.#tierFromUrl();
    this.#applyColorFromUrl();
    this.#bindTiers();
    this.#bindSlippers();
    this.#bindAddToCart();
    this.#render();
  }

  /**
   * Add to cart (BRIEF §5.5, Path A).
   *
   * Posts every monster in one cart/add.js request with items:[…]. One request
   * rather than N sequential adds: N requests can interleave and leave a
   * partially-filled cart if one fails midway, and each would fire its own
   * cart-update event, making the drawer flicker through intermediate states.
   */
  #bindAddToCart() {
    const button = this.querySelector('[data-atc]');
    if (!button) return;

    button.addEventListener('click', async () => {
      if (this.dataset.valid === 'false' || this.#busy) return;

      const items = this.lines;
      if (items.length === 0) return;

      this.#setBusy(true);
      this.#showCartError('');

      try {
        /*
          Ask for the cart section HTML in the same request, exactly as
          product-form.js does: it collects every cart-items-component's
          data-section-id and appends them as `sections`. Without this the
          response carries no markup for the drawer to repaint with, so even a
          correctly shaped event would leave the drawer showing stale contents.
        */
        const sectionIds = [];
        for (const item of document.querySelectorAll('cart-items-component')) {
          if (item instanceof HTMLElement && item.dataset.sectionId) {
            sectionIds.push(item.dataset.sectionId);
          }
        }

        const response = await fetch(`${window.Theme?.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: sectionIds.join(',') }),
        });

        const payload = await response.json();

        // cart/add.js answers 4xx with a body describing the problem rather
        // than throwing, so the status has to be checked explicitly.
        if (!response.ok) {
          throw new Error(payload?.description || payload?.message || 'Could not add to cart.');
        }

        await this.#announceAdd(payload, items);
      } catch (error) {
        /*
          Raw exception text goes to the console, never to the customer. This
          slot printed "Currency code is required with currency style"
          verbatim — an Intl TypeError shown to a shopper, which says nothing
          useful and looks broken.
        */
        console.error('[monster] add to cart failed', error);
        this.#showCartError(this.#errorMessage);
      } finally {
        this.#setBusy(false);
      }
    });
  }

  /**
   * Publish the add the way Horizon's own product form does.
   *
   * The previous version dispatched a hand-rolled CustomEvent carrying
   * everything in `detail`, and that is precisely why the add was silent:
   * cart-drawer.js gates its auto-open on `event.action === 'add'` — a
   * property on the Event SUBCLASS, not on detail — so a generic CustomEvent
   * can never satisfy it. The drawer was correctly ignoring us.
   *
   * CartLinesUpdateEvent is a real class from Shopify's hosted events module,
   * the same one product-form.js imports, and it carries
   * createCartFromAjaxResponse() to build the payload the drawer and the
   * header badge expect. Reusing it is the whole fix — no parallel refresh.
   */
  async #announceAdd(payload, items) {
    const { CartLinesUpdateEvent } = await import('@shopify/events');

    /*
      createCartFromAjaxResponse expects a FULL CART, not the add response.

      product-form.js does not pass its add response either — it calls
      #refreshCart(), which fetches `${cart_url}.json`, and hands that over.
      The cart/add.js response is the added-items shape and carries no
      `currency`, so formatting inside createCartFromAjaxResponse hit
      Intl.NumberFormat with style:'currency' and no code and threw
      "Currency code is required with currency style" — caught by our own
      failure path, which meant the event never dispatched and the badge and
      drawer never heard the add. The add itself had already succeeded
      server-side, which is why a refresh showed the item.

      So: fetch the cart the way Horizon does, and let its own helper read the
      currency from it.
    */
    const cartUrl = window.Theme?.routes?.cart_url || '/cart';
    const cart = await fetch(`${cartUrl}.json`, {
      headers: { Accept: 'application/json' },
    }).then((response) => response.json());

    /*
      The constructor takes a PROMISE, not a resolved cart: internally it runs
      normalizeCartPromise(), which calls .then() on what it is given. Passing
      the cart object directly threw "Cannot read properties of undefined
      (reading 'then')" — which our catch then reported as a failed add even
      though the add had succeeded.

      product-form.js does the same thing: it constructs the event with
      `promise: deferredEventPromise.promise` and resolves that later with
      { cart, detail }. We already have the cart, so the promise is
      pre-resolved.
    */
    const resolved = Promise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: false,
        items: payload.items ?? [payload],
        source: 'monster-configurator',
        sections: payload.sections,
      },
    });

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: String(item.id),
          quantity: item.quantity,
        })),
        promise: resolved,
      })
    );
  }

  /**
   * Pending state. The #busy flag is checked synchronously at the top of the
   * click handler, BEFORE any await, so a rapid double-click cannot slip a
   * second request through the gap while the first is in flight — disabling
   * the button alone would not be enough, since the second click can land
   * before the attribute is applied.
   */
  #setBusy(busy) {
    this.#busy = busy;

    const button = this.querySelector('[data-atc]');
    const label = this.querySelector('[data-atc-label]');
    if (!button) return;

    button.disabled = busy;
    button.setAttribute('aria-busy', busy ? 'true' : 'false');

    if (label) {
      if (busy) {
        label.textContent = this.#pendingLabel;
      } else {
        this.#renderAtcLabel();
      }
    }
  }

  #showCartError(message) {
    const node = this.querySelector('[data-cart-error]');
    if (!node) return;
    node.textContent = message;
    node.hidden = !message;
  }

  /**
   * ?color=Swamp%20Green deep links from the collection page's colorway grid.
   *
   * Sets the FIRST monster's colour and nothing else. In particular it does
   * not touch size, and the grid deliberately does not link with ?variant=:
   * a variant id resolves colour AND size, which would land the shopper on a
   * silently preselected size and undercut the height guidance that is the
   * store's main defence against size-driven returns. Colour is a look you
   * can pick from a photograph; size is not.
   *
   * First monster only, because a colorway card names one colour and Solo is
   * the resting tier, so there is exactly one line to fill. Arriving with
   * ?tier=squad&color=Cherry gives a Cherry first monster and leaves the rest
   * at their staggered defaults, which is the sensible reading of both.
   *
   * Unknown or misspelled colours are ignored rather than defaulted — a URL
   * that says Cherry must never quietly produce a Midnight.
   */
  #applyColorFromUrl() {
    const wanted = new URLSearchParams(window.location.search).get('color');
    if (!wanted) return;

    const target = wanted.trim().toLowerCase();
    const match = this.#data.colors.find((color) => color.name.toLowerCase() === target);
    if (!match || !this.#units[0]) return;

    this.#units[0].color = match.name;
  }

  /** ?tier=duo|squad deep links from the homepage cards and ads (§5.4). */
  #tierFromUrl() {
    const wanted = new URLSearchParams(window.location.search).get('tier');
    if (!wanted) return 0;

    const index = this.#data.tiers.findIndex(
      (tier) => tier.name.toLowerCase().replace(/[^a-z]/g, '').startsWith(wanted.toLowerCase())
    );
    return index === -1 ? 0 : index;
  }

  #bindTiers() {
    this.querySelectorAll('[data-tier]').forEach((card, index) => {
      card.addEventListener('click', (event) => {
        // A click on a select inside the open card must not re-fire tier
        // selection — re-rendering would destroy the dropdown mid-interaction.
        if (event.target.closest('select')) return;

        // Skip no-op re-selects for the same reason.
        if (index === this.#tierIndex) return;

        this.#tierIndex = index;
        this.#render();
      });
    });
  }

  #bindSlippers() {
    const toggle = this.querySelector('[data-slippers-input]');
    if (!toggle) return;

    toggle.addEventListener('change', () => {
      this.#slippers = toggle.checked;
      this.#render();
    });
  }

  get #tier() {
    return this.#data.tiers[this.#tierIndex];
  }

  get #setValue() {
    return this.#slippers ? this.#data.sets.slippers : this.#data.sets.base;
  }

  /** The variant for a given colour/size under the current Set, if any. */
  #variantFor(color, size) {
    return this.#data.variants.find(
      (variant) => variant.color === color && variant.size === size && variant.set === this.#setValue
    );
  }

  #render() {
    this.#renderTiers();
    this.#renderPrice();
    this.#renderPerks();
    this.#renderAddons();
    this.#renderAtcLabel();
    this.#renderValidity();
    this.#announceColor();
  }

  /**
   * The perk line (§5.3). Each perk carries the lowest tier INDEX that
   * reveals it, so the line grows as the shopper moves up the tiers —
   * matching the mockup's `+p.dataset.min <= i`.
   *
   * Index, not unit count, because that is what the mockup keys on and what
   * the merchant picks in the Theme Editor ("from the 2nd tier up"). A tier
   * whose unit count changes therefore does not silently re-gate the perks.
   *
   * Separators are CSS, so nothing here has to know which perk is visible
   * first.
   */
  #renderPerks() {
    this.querySelectorAll('[data-perks] span').forEach((perk) => {
      const min = Number.parseInt(perk.dataset.perkMin ?? '0', 10);
      perk.classList.toggle('is-on', (Number.isNaN(min) ? 0 : min) <= this.#tierIndex);
    });
  }

  /**
   * The button carries the live total, per the mockup ("Add to cart — $99").
   *
   * Formatting note: the mockup drops the decimals on whole amounts ($99, but
   * $124.98). We keep two decimals so the button agrees with the price line
   * directly above it — two different renderings of the same number, a few
   * pixels apart, reads as a bug — and because the shared formatter is
   * currency-aware rather than assuming a dollar sign.
   */
  #renderAtcLabel() {
    const label = this.querySelector('[data-atc-label]');
    if (!label) return;

    const tier = this.#tier;
    const total = tier.price + (this.#slippers ? this.#data.slippersDelta * tier.units : 0);

    label.textContent = `${this.#atcLabelBase} — ${this.#money(total)}`;
  }

  #renderTiers() {
    this.querySelectorAll('[data-tier]').forEach((card, index) => {
      const selected = index === this.#tierIndex;
      card.classList.toggle('is-selected', selected);
      card.setAttribute('aria-checked', selected ? 'true' : 'false');

      const config = card.querySelector('[data-tier-config]');
      if (!config) return;

      if (!selected) {
        config.hidden = true;
        config.replaceChildren();
        return;
      }

      config.hidden = false;
      this.#renderLines(config, this.#data.tiers[index].units);
    });
  }

  /** One `cline` per unit: number, live colour dot, colour select, size select. */
  #renderLines(container, units) {
    container.replaceChildren();
    container.classList.toggle('is-single', units === 1);

    for (let i = 0; i < units; i++) {
      const unit = this.#units[i];
      const line = document.createElement('div');
      line.className = 'monster-cline';

      const number = document.createElement('span');
      number.className = 'monster-cline__num';
      number.textContent = String(i + 1);

      const dot = document.createElement('span');
      dot.className = 'monster-cline__dot';
      dot.style.background = this.#hexFor(unit.color);

      const colorSelect = this.#select({
        values: this.#data.colors.map((color) => color.name),
        selected: unit.color,
        label: `Monster ${i + 1} color`,
        // Would this colour, at this line's current size, resolve to a variant?
        probe: (value) => this.#variantFor(value, unit.size),
        onChange: (value) => {
          this.#units[i].color = value;
          this.#render();
        },
      });

      const sizeSelect = this.#select({
        values: this.#data.sizes,
        selected: unit.size,
        label: `Monster ${i + 1} size`,
        // §5.6: the height range shows inline beside each letter so nobody
        // picks by letter alone. This is the primary defence against
        // size-driven returns — do not "tidy" it back to bare letters.
        // The option VALUE stays the plain size; only the label carries the
        // range, so nothing downstream has to parse it back out.
        labelFor: (value, index) => {
          const range = this.#data.sizeRanges?.[index];
          return range ? `${value} · ${range.trim()}` : value;
        },
        probe: (value) => this.#variantFor(unit.color, value),
        onChange: (value) => {
          this.#units[i].size = value;
          this.#render();
        },
      });
      sizeSelect.classList.add('monster-cline__size');

      const variant = this.#variantFor(unit.color, unit.size);
      line.classList.toggle('is-invalid', !variant || !variant.available);

      line.append(number, dot, colorSelect, sizeSelect);
      container.append(line);
    }
  }

  /**
   * @param {object} config
   * @param {string[]} config.values
   * @param {string} config.selected
   * @param {string} config.label
   * @param {(value: string) => any} config.probe - Resolves the variant this
   *   value would produce for THIS line. Passed in rather than inferred, so
   *   the select never has to guess whether it is a colour or a size.
   * @param {(value: string) => void} config.onChange
   */
  #select({ values, selected, label, probe, onChange, labelFor }) {
    const select = document.createElement('select');
    select.setAttribute('aria-label', label);

    values.forEach((value, index) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = labelFor ? labelFor(value, index) : value;
      option.selected = value === selected;

      // Disable combinations with no purchasable variant (§5.4). Under the
      // store's "continue selling" policy this stays inert, which is correct:
      // zero stock is not the same thing as unavailable, and this must not
      // become a permanent sold-out state.
      const variant = probe(value);
      if (!variant || !variant.available) {
        option.disabled = true;
        option.title = 'Sold out';
      }

      select.append(option);
    });

    select.addEventListener('change', () => onChange(select.value));
    return select;
  }

  #hexFor(colorName) {
    return this.#data.colors.find((color) => color.name === colorName)?.hex ?? 'transparent';
  }

  #renderPrice() {
    const tier = this.#tier;
    const money = this.#money;

    // Slippers are priced per unit on top of the tier price (§5.4b).
    const slippersTotal = this.#slippers ? this.#data.slippersDelta * tier.units : 0;
    const total = tier.price + slippersTotal;
    const compare = this.#data.tiers[0].price * tier.units + slippersTotal;

    const current = this.querySelector('[data-price-current]');
    const was = this.querySelector('[data-price-was]');
    const per = this.querySelector('[data-price-per]');

    if (current) current.textContent = money(total);

    if (was) {
      const show = compare > total;
      was.hidden = !show;
      if (show) was.textContent = money(compare);
    }

    if (per) {
      const show = tier.units > 1;
      per.hidden = !show;
      if (show) per.textContent = `${money(Math.round(total / tier.units))} each`;
    }
  }

  #renderAddons() {
    const units = this.#tier.units;

    // ×n badges appear only on multi-unit tiers (§5.4b).
    this.querySelectorAll('[data-addon-qty]').forEach((badge) => {
      badge.hidden = units < 2;
      badge.textContent = `×${units}`;
    });

    const price = this.querySelector('[data-slippers-price]');
    if (price) {
      const each = units > 1 ? ' each' : '';
      price.textContent = `+${this.#money(this.#data.slippersDelta)}${each}`;
    }
  }

  #renderValidity() {
    const units = this.#tier.units;
    const broken = [];

    for (let i = 0; i < units; i++) {
      const unit = this.#units[i];
      const variant = this.#variantFor(unit.color, unit.size);
      if (!variant || !variant.available) broken.push(i + 1);
    }

    const valid = broken.length === 0;
    this.dataset.valid = valid ? 'true' : 'false';

    const atc = this.querySelector('[data-atc]');
    if (atc && !this.#busy) atc.disabled = !valid;

    const message = this.querySelector('[data-invalid-message]');
    if (message) {
      message.hidden = valid;
      message.textContent = valid
        ? ''
        : `Monster ${broken.join(' and ')} ${broken.length > 1 ? 'are' : 'is'} not available in that combination. Pick another colour or size.`;
    }

    // Stage 7 replaces the stub with the real multi-line cart POST; this is
    // the flag it will read.
    this.dispatchEvent(
      new CustomEvent('monster:validity', { bubbles: true, detail: { valid } })
    );
  }

  /**
   * Tell the gallery which colour to show — the first line wins on multi (§5.2).
   *
   * Only on an actual change. #render() runs for every tier click and slippers
   * toggle, and firing each time would drag the gallery back to the first
   * line's colour — overriding a thumbnail the customer had just picked, for
   * an event that carried no new information.
   */
  #announceColor() {
    const color = this.#units[0]?.color;
    if (!color || color === this.#announcedColor) return;

    this.#announcedColor = color;
    document.dispatchEvent(new CustomEvent('monster:color', { detail: { color } }));
  }

  /**
   * The cart payload: one line per monster, at the chosen Set variant.
   *
   * Identical variants are MERGED into a single line with quantity, per §5.5.
   * Two Cherry / L monsters are one line of quantity 2, not two lines of 1 —
   * cart/add.js would otherwise still merge them server-side, and sending
   * them separately makes the request misrepresent what is being bought.
   * Fulfillment is unaffected either way: the line still carries the full
   * "Cherry / L / Onesie + Comb" title the agent reads.
   */
  get lines() {
    const units = this.#tier.units;
    const byVariant = new Map();

    for (let i = 0; i < units; i++) {
      const unit = this.#units[i];
      const variant = this.#variantFor(unit.color, unit.size);
      if (!variant) continue;

      byVariant.set(variant.id, (byVariant.get(variant.id) ?? 0) + 1);
    }

    return Array.from(byVariant, ([id, quantity]) => ({ id, quantity }));
  }
}

if (!customElements.get('monster-configurator')) {
  customElements.define('monster-configurator', MonsterConfigurator);
}
